import { addDays, format, getDay } from "date-fns";

export interface DivisionScheduleInput {
  id: string;
  play_schedule: Record<string, { start: string; end: string; court_ids?: string[] }[]>;
}

export interface ScheduleInput {
  matches: { id: string; round: number; home_team_id: string; away_team_id: string; division_id?: string }[];
  playSchedule: Record<string, { start: string; end: string }[]>;
  courts: { id: string }[];
  matchDuration: number; // minutes
  startDate: string; // yyyy-MM-dd
  endDate: string;
  existingBookings: { court_id: string; date: string; startMin: number; endMin: number }[];
  existingMatches: { court_id: string; date: string; time: string; home_team_id: string; away_team_id: string }[];
  divisionPlaySchedule?: Record<string, { start: string; end: string; court_ids?: string[] }[]> | null;
  divisions?: DivisionScheduleInput[];
  teamPreferences?: Record<string, {
    unavailable_days?: string[] | null;
    unavailable_dates?: string[] | null;
  }>;
}

export interface Placement {
  matchId: string;
  courtId: string;
  date: string;
  time: string;
}

const JS_DAY_TO_KEY: Record<number, string> = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  0: "sunday",
};

function toMinutes(t: string) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}

function minutesToTime(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function overlaps(aStart: number, aDur: number, bStart: number, bDur: number) {
  return aStart < bStart + bDur && bStart < aStart + aDur;
}

/**
 * Generate all available slots for each day in the league period,
 * based on the weekly play_schedule pattern.
 *
 * Each emitted slot carries the court_ids restriction from the SOURCE BLOCK
 * that produced it. A division play_schedule may have multiple blocks per
 * weekday with different court_ids (e.g. 17:30–19:00 [Bana 1] and
 * 20:30–22:00 [Bana 1, Bana 2]); preserving per-slot courtIds is what
 * lets autoSchedule honor the configured `slot.court_ids` contract.
 *
 * If divisionPlaySchedule is null/undefined the league-level playSchedule
 * is used (no court_ids on those blocks → slot.courtIds is undefined →
 * all global courts allowed at runtime).
 */
function generateDaySlots(
  startDate: string,
  endDate: string,
  playSchedule: Record<string, { start: string; end: string }[]>,
  matchDuration: number,
  divisionPlaySchedule?: Record<string, { start: string; end: string; court_ids?: string[] }[]> | null,
): { date: string; slots: { time: string; courtIds?: string[] }[] }[] {
  const effectiveSchedule = divisionPlaySchedule ?? playSchedule;
  const isDivisionSchedule = divisionPlaySchedule != null;
  const result: { date: string; slots: { time: string; courtIds?: string[] }[] }[] = [];
  let current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    const jsDay = getDay(current);
    const dayKey = JS_DAY_TO_KEY[jsDay];
    const blocks = effectiveSchedule[dayKey];

    if (blocks && blocks.length > 0) {
      // Per-time court restriction. Only meaningful when divisionPlaySchedule
      // is in effect — league-level playSchedule blocks carry no court_ids.
      // Map value `null` means "no court_ids for this time" (unrestricted at
      // runtime); a Set means a restricted list. If two blocks produce the
      // same time, court_ids union; if one of them lacks court_ids the result
      // is unrestricted (most-permissive wins).
      const courtsByTime = new Map<string, Set<string> | null>();
      const orderedTimes: string[] = [];
      for (const block of blocks) {
        const blockStart = toMinutes(block.start);
        const blockEnd = toMinutes(block.end);
        const blockCourtIds =
          isDivisionSchedule
          && "court_ids" in block
          && (block as { court_ids?: string[] }).court_ids
          && (block as { court_ids: string[] }).court_ids.length > 0
            ? (block as { court_ids: string[] }).court_ids
            : null;
        for (let t = blockStart; t + matchDuration <= blockEnd; t += matchDuration) {
          const time = minutesToTime(t);
          if (!courtsByTime.has(time)) {
            orderedTimes.push(time);
            courtsByTime.set(time, blockCourtIds ? new Set(blockCourtIds) : null);
          } else if (blockCourtIds) {
            const existing = courtsByTime.get(time);
            if (existing) {
              for (const cid of blockCourtIds) existing.add(cid);
            }
            // existing === null (unrestricted) — most-permissive wins, leave alone
          } else {
            // This block has no court_ids → unrestricted wins
            courtsByTime.set(time, null);
          }
        }
      }

      const slots = orderedTimes.map((time) => {
        const set = courtsByTime.get(time);
        return set ? { time, courtIds: Array.from(set) } : { time };
      });

      if (slots.length > 0) {
        result.push({
          date: format(current, "yyyy-MM-dd"),
          slots,
        });
      }
    }

    current = addDays(current, 1);
  }

  return result;
}

function isTeamAvailable(teamId: string, date: string, dayKey: string, teamPreferences?: ScheduleInput["teamPreferences"]): boolean {
  if (!teamPreferences) return true;
  const pref = teamPreferences[teamId];
  if (!pref) return true;
  if (pref.unavailable_days?.includes(dayKey)) return false;
  if (pref.unavailable_dates?.includes(date)) return false;
  return true;
}

export function autoSchedule(input: ScheduleInput): Placement[] {
  const { matches, playSchedule, courts, matchDuration, startDate, endDate, existingBookings, existingMatches, divisionPlaySchedule, divisions, teamPreferences } = input;

  if (courts.length === 0 || matches.length === 0) return [];

  // Build division play_schedule lookup
  const divScheduleMap = new Map<string, Record<string, { start: string; end: string; court_ids?: string[] }[]>>();
  if (divisions) {
    for (const d of divisions) {
      if (d.play_schedule && Object.keys(d.play_schedule).length > 0) {
        divScheduleMap.set(d.id, d.play_schedule);
      }
    }
  }

  // Track what's already placed (existing + new placements)
  const occupiedSlots = new Set<string>(); // "courtId|date|time"
  const teamDayMap = new Set<string>(); // "teamId|date"

  // Seed with existing matches
  for (const em of existingMatches) {
    occupiedSlots.add(`${em.court_id}|${em.date}|${em.time}`);
    teamDayMap.add(`${em.home_team_id}|${em.date}`);
    teamDayMap.add(`${em.away_team_id}|${em.date}`);
  }

  // Seed with existing bookings
  const bookingsByDateCourt = new Map<string, { startMin: number; endMin: number }[]>();
  for (const b of existingBookings) {
    const key = `${b.court_id}|${b.date}`;
    if (!bookingsByDateCourt.has(key)) bookingsByDateCourt.set(key, []);
    bookingsByDateCourt.get(key)!.push({ startMin: b.startMin, endMin: b.endMin });
  }

  function isSlotFree(courtId: string, date: string, time: string): boolean {
    if (occupiedSlots.has(`${courtId}|${date}|${time}`)) return false;

    const slotStart = toMinutes(time);
    const bookings = bookingsByDateCourt.get(`${courtId}|${date}`) ?? [];
    return !bookings.some((b) => overlaps(slotStart, matchDuration, b.startMin, b.endMin - b.startMin));
  }

  function isTeamFree(teamId: string, date: string): boolean {
    return !teamDayMap.has(`${teamId}|${date}`);
  }

  const placements: Placement[] = [];

  // Group matches by division_id (or "none" for unassigned)
  const matchesByDivision = new Map<string, typeof matches>();
  for (const m of matches) {
    const key = m.division_id ?? "__none__";
    if (!matchesByDivision.has(key)) matchesByDivision.set(key, []);
    matchesByDivision.get(key)!.push(m);
  }

  // Schedule each division's matches using its own play_schedule
  for (const [divId, divMatches] of matchesByDivision) {
    // Determine effective schedule for this division
    const divSpecificSchedule = divId !== "__none__" ? divScheduleMap.get(divId) : undefined;
    // Fall back: divisionPlaySchedule (legacy single-division override), then league-level playSchedule
    const effectiveDivSchedule = divSpecificSchedule ?? divisionPlaySchedule ?? undefined;

    const daySlots = generateDaySlots(startDate, endDate, playSchedule, matchDuration, effectiveDivSchedule);

    // Sort by round
    const sorted = [...divMatches].sort((a, b) => a.round - b.round);

    // Group by round
    const rounds = new Map<number, typeof sorted>();
    for (const m of sorted) {
      if (!rounds.has(m.round)) rounds.set(m.round, []);
      rounds.get(m.round)!.push(m);
    }

    // Place round by round
    for (const [, roundMatches] of [...rounds.entries()].sort((a, b) => a[0] - b[0])) {
      const remaining = [...roundMatches];

      for (const daySlot of daySlots) {
        if (remaining.length === 0) break;

        for (const slot of daySlot.slots) {
          if (remaining.length === 0) break;

          const time = slot.time;

          // Honor per-slot court_ids: if the slot specifies a restricted
          // court list, candidates = intersection with global courts.
          // If unspecified (legacy block / league-level fallback), allow
          // all global courts.
          const availableCourts = slot.courtIds
            ? courts.filter((c) => slot.courtIds!.includes(c.id))
            : courts;

          for (const court of availableCourts) {
            if (remaining.length === 0) break;
            if (!isSlotFree(court.id, daySlot.date, time)) continue;

            // Find a match whose teams are both free this day
            const dayKey = JS_DAY_TO_KEY[getDay(new Date(daySlot.date))];
            const matchIdx = remaining.findIndex(
              (m) => isTeamFree(m.home_team_id, daySlot.date) && isTeamFree(m.away_team_id, daySlot.date)
                && isTeamAvailable(m.home_team_id, daySlot.date, dayKey, teamPreferences)
                && isTeamAvailable(m.away_team_id, daySlot.date, dayKey, teamPreferences)
            );

            if (matchIdx === -1) continue;

            const match = remaining.splice(matchIdx, 1)[0];
            placements.push({ matchId: match.id, courtId: court.id, date: daySlot.date, time });
            occupiedSlots.add(`${court.id}|${daySlot.date}|${time}`);
            teamDayMap.add(`${match.home_team_id}|${daySlot.date}`);
            teamDayMap.add(`${match.away_team_id}|${daySlot.date}`);
          }
        }
      }
    }
  }

  return placements;
}
